import { Breadcrumb, HeaderToolbar } from "@xola/ui-kit";
import { isMistPurchase } from "common";
import { concat, uniqBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Permission, Purchase, PurchaseStatus } from "types";
import shallow from "zustand/shallow";
import { CreatePurchaseButton } from "../components/Common/CreatePurchaseButton";
import { ColumnLayout } from "../components/Layout/ColumnLayout";
import {
    AdvancedSearch,
    getPurchaseFilterParams,
    PurchaseFilterList,
} from "../components/PurchasesMenu/PurchaseFilterList";
import { PurchaseFilterTagList } from "../components/PurchasesMenu/PurchaseFilterTagList";
import { PurchaseListLayout } from "../components/PurchasesMenu/PurchaseListLayout";
import { PurchaseSearch } from "../components/PurchasesMenu/PurchaseSearch";
import { SellerBalance } from "../components/PurchasesMenu/SellerBalance";
import { EmbeddedAppTriggerLocation } from "../embeddedApps/EmbeddedAppTriggerLocation";
import { useAuthorization } from "../hooks/useAuthorization";
import { useInfiniteCollection } from "../hooks/useInfiniteCollection";
import { useResource } from "../hooks/useResource";
import { useVisibility } from "../hooks/useVisibility";
import { getCustomerName } from "../models/Purchase";
import { usePurchaseFilterStore } from "../stores/purchaseFilterStore";
import { usePurchasePageStore } from "../stores/usePurchasePageStore";
import { Page } from "./Page";
import { getPurchaseSearchTerm } from "@/helpers/purchaseSearchHelper";
import { usePurchase } from "@/hooks/resources/usePurchase";
import { useUser } from "@/hooks/useAccount";
import { useAffiliatePurchaseScope } from "@/hooks/useAffiliatePurchaseScope";
import { usePurchaseSearchStore } from "@/stores/purchaseSearchStore";
import { getSeller } from "@/utils/getSeller";

const getUrl = (purchase: Purchase) => {
    return isMistPurchase(purchase) ? `/purchases/${purchase.id}` : `/purchases/${purchase.id}/${purchase.items[0].id}`;
};

const getIncludeParams = (expandPartner = false) => {
    return {
        include: "form",
        expand: expandPartner ? "waiver,partner" : "waiver",
    };
};

// The affiliate list query is scoped server-side, but purchases fetched by id (the selected
// purchase from the URL and the contact-timeline purchase) bypass that filter, so re-check them
// against the allowed partner scope. An empty set means scoping is inactive — either the user is
// not an affiliate, or the affiliate record is still loading.
const isWithinAffiliateScope = (allowedPartnerIdsSet: Set<ID>, purchase?: Purchase) => {
    if (allowedPartnerIdsSet.size === 0) {
        return true;
    }

    return !!purchase?.partner?.id && allowedPartnerIdsSet.has(purchase.partner.id);
};

export const PurchaseItemPage = () => {
    const history = useHistory();
    const [search, setSearch] = useState("");
    const { isGranted } = useAuthorization();
    const searchTerm = getPurchaseSearchTerm(search);
    const shouldSearch = !!searchTerm;
    const user = useUser();
    const seller = getSeller();
    const sellerHasCasEnabled = seller?.preferences?.crossAccountSelling?.enabled ?? false;

    const lastSelectedPurchase = usePurchasePageStore((state) => state.lastSelectedPurchase);
    const lastSelectedItem = usePurchasePageStore((state) => state.lastSelectedItem);

    const { id = lastSelectedPurchase, itemId = lastSelectedItem } = useParams<{ id?: ID; itemId?: ID }>();

    const { data: oldPurchase, isLoading: isLoadingOldPurchase } = useResource<Purchase>(
        ["purchases", id],
        getIncludeParams(sellerHasCasEnabled),
    );

    const { data: newPurchase, isLoading: isLoadingNewPurchase } = useResource<Purchase>(
        ["purchases", id],
        getIncludeParams(sellerHasCasEnabled), // Cache for 1s so that its always re-fetched when the page loads
        { staleTime: 1000 },
    );

    const purchase = newPurchase ?? oldPurchase;

    const purchaseContext = useMemo(() => {
        if (!id) return undefined;
        return {
            purchases: [
                {
                    id,
                    items: (purchase?.items ?? []).map((purchaseItem) => ({
                        id: purchaseItem.id,
                        guests: ("guests" in purchaseItem ? purchaseItem.guests : []).map((g: { id: string }) => ({
                            id: g.id,
                        })),
                    })),
                },
            ],
        };
    }, [id, purchase]);

    const item = purchase?.items.find((item) => item.id === itemId);
    const [isAdvancedSearchVisible, { toggle }] = useVisibility(false);
    const [from, to] = usePurchaseFilterStore((state) => [state.from, state.to], shallow);
    const [filters, resetToDefaults, resetFilters, resetDate] = usePurchaseFilterStore(
        (state) => [state.filters, state.resetToDefaults, state.resetFilters, state.resetDate],
        shallow,
    );
    const [isRealTimeRefetch, setIsRealTimeRefetch] = usePurchaseFilterStore(
        (state) => [state.isRealTimeRefetch, state.setIsRealTimeRefetch],
        shallow,
    );

    const [searchContactPurchase, setContactPurchaseId] = usePurchaseSearchStore((state) => [
        state.searchContactPurchase,
        state.setContactPurchaseId,
    ]);

    const { isAffiliateScopeReady, allowedPartnerIdsSet, partnerFilter } = useAffiliatePurchaseScope(user);

    const filterParams = useMemo(() => {
        return { ...getPurchaseFilterParams(from, to, filters), ...partnerFilter };
    }, [from, to, filters, partnerFilter]);

    const fetchParams: any = {
        status: PurchaseStatus.COMMITTED,
        limit: 20,
        ...partnerFilter,
    };
    if (shouldSearch && !isAdvancedSearchVisible) {
        fetchParams.search = searchTerm;
    }

    const {
        data: purchasesData,
        hasNextPage = false,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
        isFetching,
    } = useInfiniteCollection<Purchase>("purchases", isAdvancedSearchVisible ? filterParams : fetchParams, {
        keepPreviousData: true,
        staleTime: 1000,
        enabled: isAffiliateScopeReady,
        onSuccess: () => {
            setIsRealTimeRefetch(false);
        },
    });

    const { data: contactPurchase } = usePurchase(searchContactPurchase);

    const isLoadingPurchases = isLoading || isLoadingNewPurchase || isLoadingOldPurchase;
    const isUnauthorizedAffiliatePurchase = !!purchase && !isWithinAffiliateScope(allowedPartnerIdsSet, purchase);

    // Guard: redirect affiliate users if they open (via direct URL) a purchase their code wasn't used on
    useEffect(() => {
        if (isUnauthorizedAffiliatePurchase) {
            usePurchasePageStore.setState({ lastSelectedPurchase: undefined, lastSelectedItem: undefined });
            setContactPurchaseId("");
            history.replace("/purchases");
        }
    }, [isUnauthorizedAffiliatePurchase, history, setContactPurchaseId]);

    // populate purchases list with the selected purchase in case it's missing to display in the list
    const purchases = useMemo(() => {
        const localPurchases = [...purchasesData];

        if (contactPurchase && isWithinAffiliateScope(allowedPartnerIdsSet, contactPurchase)) {
            localPurchases.push(contactPurchase);
        }

        // not showing the selected purchase alone if the list is loading or if the search term is provided
        if (isLoadingPurchases || shouldSearch || isAdvancedSearchVisible) {
            return localPurchases;
        }

        const isSelectedPurchaseAlreadyListed = localPurchases.some(
            (purchaseData) => purchaseData?.id === purchase?.id,
        );

        if (isSelectedPurchaseAlreadyListed) {
            return localPurchases;
        }

        return concat(purchase ?? [], localPurchases);
    }, [
        isLoadingPurchases,
        purchasesData,
        contactPurchase,
        purchase,
        shouldSearch,
        isAdvancedSearchVisible,
        allowedPartnerIdsSet,
    ]);

    const sortedPurchases = useMemo(() => {
        const uniquePurchases = uniqBy(purchases, "id");

        return uniquePurchases.sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf());
    }, [purchases]);

    const handleClick = () => {
        if (isAdvancedSearchVisible) {
            resetFilters();
            resetDate();
        } else {
            resetToDefaults();
        }

        toggle();
    };

    useEffect(() => {
        if (purchases.length === 0) {
            return;
        }

        if (!id) {
            /** User is directly coming to `/purchases` url, we need to append :id (and :itemId) of first purchase item in URL.
             *  We need to exclude partner item purchases here */
            const firstNonPartnerItemPurchase = purchases.find((purchase) => !!purchase.items);
            if (firstNonPartnerItemPurchase) {
                history.push(getUrl(firstNonPartnerItemPurchase));
            }
            return;
        }

        if (!itemId && purchase && purchase.id === id && !isMistPurchase(purchase)) {
            /** We have PurchaseId in URL, now we need to check if this is single item purchase and :itemId is not available in URL, we need to append it */
            history.push(getUrl(purchase));
        }
    }, [id, itemId, purchases, purchase, history]);

    useEffect(() => {
        if (id && itemId) {
            usePurchasePageStore.setState({ lastSelectedPurchase: id, lastSelectedItem: itemId });
        }
    }, [id, itemId, purchases]);

    useEffect(() => {
        if (contactPurchase) {
            setContactPurchaseId("");
        }
    }, [setContactPurchaseId, contactPurchase, purchases]);

    useEffect(() => {
        // When filters change, update the selected purchase to the first one in the filtered list only if the selected purchase is not in the list.
        if (
            isAdvancedSearchVisible &&
            purchases.length > 0 &&
            (!purchase || !purchases.some((index) => index.id === purchase?.id))
        ) {
            history.push(getUrl(purchases[0]));
        }
    }, [isAdvancedSearchVisible, purchase, purchases, history]);

    const isBalanceVisible = isGranted(Permission.BALANCE_VIEW);

    if (isUnauthorizedAffiliatePurchase) {
        return null;
    }

    return (
        <Page
            isDynamicTitle={!!purchase}
            title={purchase ? getCustomerName(purchase) : "Purchases"}
            className="page:purchases"
        >
            <ColumnLayout className="print:!h-auto print:!max-h-full">
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Purchases</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                    {!isAdvancedSearchVisible && <PurchaseSearch isLoading={isFetching} onSearchText={setSearch} />}
                    <AdvancedSearch isPurchaseFilterOpen={isAdvancedSearchVisible} onClick={handleClick} />
                    <div className="flex-grow" />
                    {isBalanceVisible && <SellerBalance />}
                    <EmbeddedAppTriggerLocation id="purchases.detail.headerBar" context={purchaseContext} />
                    <CreatePurchaseButton />
                </HeaderToolbar>
                <PurchaseFilterList isOpen={isAdvancedSearchVisible} />
                {isAdvancedSearchVisible && <PurchaseFilterTagList />}
                <ColumnLayout.Fill>
                    {(height: string) => (
                        <PurchaseListLayout
                            purchases={sortedPurchases}
                            isLoadingPurchases={isRealTimeRefetch ? false : isFetching || isLoading}
                            isFetchingNextPage={isFetchingNextPage}
                            hasNextPage={hasNextPage}
                            purchase={purchase}
                            item={item}
                            height={height}
                            isAdvancedSearch={isAdvancedSearchVisible}
                            fetchNextPage={fetchNextPage}
                        />
                    )}
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
